import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Mail, Lock, Building2, User, Phone, MapPin, Briefcase, ShieldCheck, Plus, Trash2 } from "lucide-react";
import { AuthShell, Field } from "./Login";

const newActivity = (category = "") => ({ category, surface_m2: 50, space_name: "" });
const newLocation = (category = "") => ({
  venue_name: "",
  county: "",
  city: "",
  business_address: "",
  is_rural: false,
  activities: [newActivity(category)],
});

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [tariffs, setTariffs] = useState([]);
  const [tariffError, setTariffError] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "",
    business_name: "", company_reg_no: "", vat_number: "",
    contact_person: "", phone: "", legal_address: "",
    terms_accepted: false, gdpr_accepted: false, electronic_docs_accepted: false,
    terms_version: "1.0", gdpr_version: "1.0", electronic_docs_version: "1.0",
  });
  const [locations, setLocations] = useState([newLocation()]);
  const allLegalAccepted = form.terms_accepted && form.gdpr_accepted && form.electronic_docs_accepted;
  const tariffCategories = tariffs.map((tr) => tr.category);

  useEffect(() => {
    let alive = true;
    api.get("/tariffs")
      .then(({ data }) => {
        if (!alive) return;
        setTariffError("");
        setTariffs(data);
        const first = data[0]?.category || "";
        setLocations([newLocation(first)]);
      })
      .catch(() => {
        if (!alive) return;
        setTariffError("Nu pot incarca tarifele momentan. Verifica daca backend-ul este pornit si reincarca pagina.");
        setTariffs([]);
        setLocations([newLocation("")]);
      });
    api.get("/legal/versions").then(({ data }) => {
      setForm((f) => ({
        ...f,
        terms_version: data.terms_version || "1.0",
        gdpr_version: data.gdpr_version || "1.0",
        electronic_docs_version: data.electronic_docs_version || "1.0",
      }));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "object" ? v.target.value : v }));
  const updateLocation = (index, patch) => setLocations((items) => items.map((loc, i) => i === index ? { ...loc, ...patch } : loc));
  const updateActivity = (locIndex, actIndex, patch) => setLocations((items) => items.map((loc, i) => {
    if (i !== locIndex) return loc;
    return { ...loc, activities: loc.activities.map((a, j) => j === actIndex ? { ...a, ...patch } : a) };
  }));
  const addLocation = () => setLocations((items) => [...items, newLocation(tariffs[0]?.category || "")]);
  const removeLocation = (index) => setLocations((items) => items.length > 1 ? items.filter((_, i) => i !== index) : items);
  const addActivity = (locIndex) => setLocations((items) => items.map((loc, i) => {
    if (i !== locIndex) return loc;
    const category = loc.activities[loc.activities.length - 1]?.category || tariffCategories[0] || "";
    if (!category) {
      toast.message(t("pricing.allActivitiesAdded"));
      return loc;
    }
    return { ...loc, activities: [...loc.activities, newActivity(category)] };
  }));
  const removeActivity = (locIndex, actIndex) => setLocations((items) => items.map((loc, i) => (
    i === locIndex && loc.activities.length > 1 ? { ...loc, activities: loc.activities.filter((_, j) => j !== actIndex) } : loc
  )));

  const submit = async (e) => {
    e.preventDefault();
    if (!allLegalAccepted) {
      toast.error(t("register.acceptAllLegal"));
      return;
    }
    const firstLocation = locations[0];
    const firstActivity = firstLocation.activities[0];
    setBusy(true);
    try {
      const payload = {
        ...form,
        venue_name: firstLocation.venue_name || form.business_name,
        county: firstLocation.county,
        city: firstLocation.city,
        business_address: firstLocation.business_address,
        activity_category: firstActivity.category,
        surface_m2: Number(firstActivity.surface_m2),
        locations: locations.map((loc) => ({
          ...loc,
          activities: loc.activities.map((a) => ({ category: a.category, surface_m2: Number(a.surface_m2), space_name: a.space_name || undefined })),
        })),
      };
      await register(payload);
      toast.success(t("register.created"));
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("register.title")} subtitle={t("register.sub")} wide>
      {tariffError && (
        <div className="mb-4 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-100" data-testid="register-tariff-load-error">
          {tariffError}
        </div>
      )}
      <form onSubmit={submit} className="grid gap-4 md:grid-cols-2" data-testid="register-form">
        <Field icon={Mail} type="email" placeholder={t("register.email")} value={form.email} setValue={set("email")} testid="register-email" />
        <Field icon={Lock} type="password" placeholder={t("register.password")} value={form.password} setValue={set("password")} testid="register-password" />
        <Field icon={Building2} placeholder={t("register.business_name")} value={form.business_name} setValue={set("business_name")} testid="register-business-name" />
        <Field icon={User} placeholder={t("register.contact_person")} value={form.contact_person} setValue={set("contact_person")} testid="register-contact-person" />
        <Field icon={Phone} placeholder={t("register.phone")} value={form.phone} setValue={set("phone")} testid="register-phone" />
        <Field icon={Briefcase} placeholder={t("register.company_reg_no")} value={form.company_reg_no} setValue={set("company_reg_no")} testid="register-reg-no" required={false} />
        <Field icon={Briefcase} placeholder={t("register.vat_number")} value={form.vat_number} setValue={set("vat_number")} testid="register-vat" />
        <div className="md:col-span-2">
          <Field icon={MapPin} placeholder={t("register.legal_address")} value={form.legal_address} setValue={set("legal_address")} testid="register-legal-address" />
        </div>

        <div className="md:col-span-2 space-y-4">
          {locations.map((loc, locIndex) => (
            <div key={locIndex} className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="font-semibold text-white">{t("pricing.venue")} {locIndex + 1}</div>
                {locations.length > 1 && (
                  <button type="button" onClick={() => removeLocation(locIndex)} className="rounded-lg border border-white/10 p-2 text-zinc-400 hover:text-red-300">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Input value={loc.venue_name} onChange={(v) => updateLocation(locIndex, { venue_name: v })} placeholder={t("pricing.venueName")} />
                <Input value={loc.county} onChange={(v) => updateLocation(locIndex, { county: v })} placeholder={t("pricing.county")} />
                <Input value={loc.city} onChange={(v) => updateLocation(locIndex, { city: v })} placeholder={t("pricing.city")} />
                <Input value={loc.business_address} onChange={(v) => updateLocation(locIndex, { business_address: v })} placeholder={t("pricing.venueAddress")} />
              </div>
              <label className="mt-3 flex items-center gap-2 rounded-xl border border-white/10 bg-[#121212] px-3 py-2 text-xs text-zinc-300">
                <input
                  type="checkbox"
                  checked={Boolean(loc.is_rural)}
                  onChange={(e) => updateLocation(locIndex, { is_rural: e.target.checked })}
                  className="accent-[#D4A23A]"
                />
                Mediu rural - aplica regula din tabel
              </label>
              <div className="mt-3 space-y-3">
                {loc.activities.map((activity, actIndex) => (
                  <div key={actIndex} className="grid gap-3 md:grid-cols-[1fr_1fr_120px_38px]">
                    <Input value={activity.space_name || ""} onChange={(v) => updateActivity(locIndex, actIndex, { space_name: v })} placeholder="Zona / subcategorie (optional)" required={false} />
                    <SelectField
                      value={activity.category}
                      onChange={(e) => updateActivity(locIndex, actIndex, { category: e.target.value })}
                      options={tariffCategories}
                      placeholder={t("pricing.chooseActivity")}
                    />
                    <Input type="number" step={1} value={activity.surface_m2} onChange={(v) => updateActivity(locIndex, actIndex, { surface_m2: Number(v) })} placeholder="mp" />
                    <button type="button" onClick={() => removeActivity(locIndex, actIndex)} disabled={loc.activities.length === 1} className="rounded-xl border border-white/10 text-zinc-400 disabled:opacity-30 hover:text-red-300">
                      <Trash2 size={15} className="mx-auto" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => addActivity(locIndex)} className="inline-flex items-center gap-2 rounded-xl border border-[#D4A23A]/30 px-3 py-2 text-xs font-semibold text-[#D4A23A]">
                  <Plus size={14} /> Adauga zona / activitate in aceeasi licenta
                </button>
              </div>
            </div>
          ))}
          <button type="button" onClick={addLocation} className="inline-flex items-center gap-2 rounded-xl bg-white/5 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10">
            <Plus size={16} /> {t("pricing.addVenue")}
          </button>
        </div>

        <div className="md:col-span-2 rounded-2xl border border-[#D4A23A]/20 bg-[#D4A23A]/5 p-4" data-testid="register-legal-consent-box">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
            <ShieldCheck size={18} className="text-[#D4A23A]" /> {t("register.requiredAgreements")}
          </div>
          <ConsentCheck checked={form.terms_accepted} onChange={(v) => setForm((f) => ({ ...f, terms_accepted: v }))} testid="register-terms-accepted">
            {t("register.acceptTerms")} <Link to="/legal/terms" target="_blank" className="text-[#D4A23A] underline">{t("register.termsLink")}</Link>.
          </ConsentCheck>
          <ConsentCheck checked={form.gdpr_accepted} onChange={(v) => setForm((f) => ({ ...f, gdpr_accepted: v }))} testid="register-gdpr-accepted">
            {t("register.acceptGdpr")} <Link to="/legal/privacy" target="_blank" className="text-[#D4A23A] underline">{t("register.gdprLink")}</Link>.
          </ConsentCheck>
          <ConsentCheck checked={form.electronic_docs_accepted} onChange={(v) => setForm((f) => ({ ...f, electronic_docs_accepted: v }))} testid="register-electronic-docs-accepted">
            {t("register.acceptElectronicDocs")}
          </ConsentCheck>
        </div>

        <button type="submit" disabled={busy || !allLegalAccepted} data-testid="register-submit-btn" className="rounded-full bg-[#D4A23A] py-3 font-semibold text-black transition hover:bg-[#E2B85C] disabled:cursor-not-allowed disabled:opacity-40 md:col-span-2">
          {busy ? t("common.loading") : t("register.submit")}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-zinc-500">
        {t("register.haveAccount")} <Link to="/login" className="text-[#D4A23A] hover:underline" data-testid="register-to-login">{t("nav.signin")}</Link>
      </p>
    </AuthShell>
  );
}

function Input({ value, onChange, placeholder, type = "text", step, required = true }) {
  return (
    <input
      required={required}
      type={type}
      step={step}
      min={type === "number" ? 1 : undefined}
      inputMode={type === "number" ? "numeric" : undefined}
      value={value}
      onFocus={(e) => { if (type === "number") e.currentTarget.select(); }}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="lime-focus w-full rounded-xl border border-white/10 bg-[#121212] px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none"
    />
  );
}

function ConsentCheck({ checked, onChange, children, testid }) {
  return (
    <label className="mb-3 flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-[#0A0A0A] p-3 text-sm text-zinc-300 transition hover:border-[#D4A23A]/40" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-[#D4A23A]" />
      <span className="leading-5">{children}</span>
    </label>
  );
}

function SelectField({ value, onChange, options, disabledOptions = new Set(), placeholder }) {
  return (
    <select required value={value} onChange={onChange} className="lime-focus w-full rounded-xl border border-white/10 bg-[#121212] px-4 py-3 text-sm text-white focus:outline-none">
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => <option key={o} value={o} disabled={disabledOptions.has(o)} className="bg-[#121212]">{o}</option>)}
    </select>
  );
}
